import { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import Logo from './Logo';
import ordnr1Image from '../assets/ordnr1.jpg';

interface SignupPageProps {
  onLogin: () => void;
}

export default function SignupPage({ onLogin }: SignupPageProps) {
  const { signup } = useAuth();
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
    if (passwordError && field === 'password') setPasswordError('');
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return t('signup.passwordTooShort');
    }
    if (!/[A-Z]/.test(password)) {
      return t('signup.passwordMissingUppercase');
    }
    if (!/[0-9]/.test(password)) {
      return t('signup.passwordMissingNumber');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return t('signup.passwordMissingSymbol');
    }
    return null;
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError(t('signup.fillAllFields'));
      return false;
    }
    const pwdError = validatePassword(formData.password);
    if (pwdError) {
      setPasswordError(pwdError);
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSignup = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');
    setPasswordError('');

    try {
      await signup(formData.email, formData.password, undefined);
      window.location.href = '/questionnaire';
    } catch (error: any) {
      setError(error.message || t('signup.signupFailed'));
      setPasswordError('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Logo */}
      <div className="auth-logo">
        <Logo className="w-12 h-12" />
      </div>
      
      <div className="auth-container">
        <div className="auth-layout">
          {/* Left Column - Form Content */}
          <div className="auth-form-column">

            {/* Header */}
            <div className="auth-header">
              <h1 className="auth-title">{t('signup.title')}</h1>
            </div>

            {/* Privacy Note */}
            <div className="auth-privacy-note">
                <p className="auth-privacy-text">
                  {t('signup.privacyNote')}
                </p>
            </div>

            {/* Main Content */}
            <div className="auth-content">
              <div className="auth-form-content">
                {error && (
                  <div className="auth-error">
                    {error}
                  </div>
                )}

                <div className="auth-input-group">
                  <Label htmlFor="email" className="auth-label">{t('signup.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder={t('signup.emailPlaceholder')}
                    className="auth-input"
                  />
                </div>

                <div className="auth-input-group">
                  <Label htmlFor="password" className="auth-label">{t('signup.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder={t('signup.passwordPlaceholder')}
                    className="auth-input"
                  />
                  {passwordError && (
                    <div className="auth-password-error">
                      {passwordError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="auth-nav">
              <Button
                onClick={handleSignup}
                disabled={isLoading}
                className="auth-button-primary"
              >
                {isLoading ? (
                  <>
                    <div className="spinner-border-sm"></div>
                    {t('signup.creatingAccount')}
                  </>
                ) : (
                  <>
                    {t('signup.createAccount')}
                  </>
                )}
              </Button>

              <div className="auth-note">
                {`${t('signup.goToLoginNote')} `}
                <a onClick={onLogin} className="auth-note-link">
                  {t('signup.goToLoginLink')}
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className="auth-visual-column">
            <div className="auth-visual-inner">
              <div className="auth-visual-content">
                <img
                  src={ordnr1Image} 
                  alt="Signup Visual" 
                  className="auth-visual-image"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}